import { useState, useMemo, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import dayjs from 'dayjs'
import { ImportItemMetadata, ItemModelOption } from '../../../types/response'
import { getItemModels, getOwners } from '../../../services'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Button, FormItem } from 'vnetwork-ui'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import ChildComponentCard, {
    ChildComponentFormData,
} from './ChildComponentCard'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import { Plus, X, Loader2, Check, ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface DateFormData {
    import_date: string
    warranty_start: string
    warranty_end: string
}

// Convert date value to RFC3339 format for Go backend
const toRFC3339 = (
    dateValue: string | Date | dayjs.Dayjs | undefined | null
): string | undefined => {
    if (!dateValue) return undefined
    const d = dayjs(dateValue)
    if (!d.isValid()) return undefined
    return d.format('YYYY-MM-DD') + 'T00:00:00Z'
}

const fromRFC3339 = (dateStr?: string): string => {
    if (!dateStr) return new Date().toISOString().split('T')[0]
    return dateStr.split('T')[0]
}

const CONDITIONS = [
    { value: 'new', label: 'New' },
    { value: 'used', label: 'Used' },
    { value: 'refurbished', label: 'Refurbished' },
]

// Props for create mode
interface CreateModeProps {
    mode: 'create'
    onAdd: (item: ImportItemMetadata) => void
    item?: never
    onCancel?: never
    models?: never
    isLoadingModels?: never
}

// Props for edit mode
interface EditModeProps {
    mode: 'edit'
    item: ImportItemMetadata
    onAdd: (item: ImportItemMetadata) => void
    onCancel: () => void
    models: ItemModelOption[]
    isLoadingModels: boolean
}

type ItemFormProps = CreateModeProps | EditModeProps

const ItemForm = (props: ItemFormProps) => {
    const { mode, onAdd } = props
    const isEditMode = mode === 'edit'

    const [showForm, setShowForm] = useState(isEditMode)
    const [hasTags, setHasTags] = useState<'yes' | 'no'>('yes')
    const [serviceTags, setServiceTags] = useState<string[]>([])
    const [quantity, setQuantity] = useState('')
    const [tagInput, setTagInput] = useState('')
    const [modelPickerOpen, setModelPickerOpen] = useState(false)
    const [modelSearch, setModelSearch] = useState('')
    const [ownerPickerOpen, setOwnerPickerOpen] = useState(false)
    const [ownerSearch, setOwnerSearch] = useState('')
    const today = new Date().toISOString().split('T')[0]

    const [formData, setFormData] = useState({
        model_id: '',
        form_factor: '',
        name: '',
        purchase_price: '',
        condition: 'new',
        owner: '',
        description: '',
    })

    const [childComponents, setChildComponents] = useState<
        ChildComponentFormData[]
    >([])

    // Fetch models (only for create mode, edit mode receives via props)
    const { data: fetchedModels = [], isLoading: fetchingModels } =
        useQueryWithHandler(
            {
                queryKey: [QUERY_KEYS.WAREHOUSE_MODELS],
                queryFn: getItemModels,
                enabled: !isEditMode && showForm,
                staleTime: 5 * 60 * 1000,
            },
            { showLoading: false, showError: false }
        )

    // Use props models for edit mode, fetched for create mode
    const models = isEditMode ? props.models : fetchedModels
    const isLoadingModels = isEditMode ? props.isLoadingModels : fetchingModels

    // Fetch owners
    const { data: owners = [] } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.WAREHOUSE_OWNERS],
            queryFn: getOwners,
            enabled: showForm,
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    const {
        control,
        getValues,
        reset: resetDates,
    } = useForm<DateFormData>({
        defaultValues: {
            import_date: today,
            warranty_start: today,
            warranty_end: today,
        },
    })

    // Initialize form when editing
    useEffect(() => {
        if (isEditMode && props.item) {
            const item = props.item
            setFormData({
                model_id: item.model_id || '',
                form_factor: '',
                name: item.name || '',
                purchase_price: item.purchase_price?.toString() || '',
                condition: item.condition || 'new',
                owner: item.owner || '',
                description: item.description || '',
            })

            if (item.service_tags && item.service_tags.length > 0) {
                setHasTags('yes')
                setServiceTags([...item.service_tags])
                setQuantity('')
            } else {
                setHasTags('no')
                setServiceTags([])
                setQuantity(item.quantity?.toString() || '1')
            }

            resetDates({
                import_date: fromRFC3339(item.purchase_date),
                warranty_start: fromRFC3339(item.warranty_start),
                warranty_end: fromRFC3339(item.warranty_end),
            })

            if (item.children && item.children.length > 0) {
                const childData: ChildComponentFormData[] = item.children.map(
                    (child) => ({
                        id: crypto.randomUUID(),
                        model_id: child.model_id || '',
                        model_label: child.name || '',
                        category: child.category || '',
                        hasTags:
                            child.service_tags && child.service_tags.length > 0
                                ? 'yes'
                                : 'no',
                        serviceTags: child.service_tags || [],
                        quantity: child.quantity?.toString() || '',
                        condition: child.condition || 'new',
                    })
                )
                setChildComponents(childData)
            } else {
                setChildComponents([])
            }

            setTagInput('')
        }
    }, [isEditMode, props.item])

    // Filter models by search and group by category
    const filteredModelsByCategory = useMemo(() => {
        const searchLower = modelSearch.toLowerCase()
        const filtered = models.filter(
            (m) =>
                m.label.toLowerCase().includes(searchLower) ||
                m.category.toLowerCase().includes(searchLower) ||
                (m.brand?.toLowerCase().includes(searchLower) ?? false)
        )

        const grouped: Record<string, ItemModelOption[]> = {}
        filtered.forEach((model) => {
            const category = model.category || 'OTHER'
            if (!grouped[category]) {
                grouped[category] = []
            }
            grouped[category].push(model)
        })
        return grouped
    }, [models, modelSearch])

    const selectedModel = useMemo(() => {
        if (!formData.model_id) return null
        return models.find((m) => m.value === formData.model_id)
    }, [formData.model_id, models])

    const componentModels = useMemo(
        () => models.filter((m) => m.item_type === 'component'),
        [models]
    )

    // Auto-fill form factor and name when model changes
    useEffect(() => {
        if (selectedModel) {
            const formFactorSpec = selectedModel.specifications?.find(
                (s) => s.name.toUpperCase() === 'FORM FACTOR'
            )
            setFormData((prev) => ({
                ...prev,
                form_factor: formFactorSpec?.value || '',
                name: selectedModel.label,
            }))
        }
    }, [selectedModel])

    // Owner combobox: filter existing owners by search; allow creating a new one
    // when the typed value matches no existing owner (case-insensitive).
    const trimmedOwnerSearch = ownerSearch.trim()
    const filteredOwners = useMemo(() => {
        if (!trimmedOwnerSearch) return owners
        const searchLower = trimmedOwnerSearch.toLowerCase()
        return owners.filter((o) => o.label.toLowerCase().includes(searchLower))
    }, [owners, trimmedOwnerSearch])
    const canCreateOwner =
        trimmedOwnerSearch.length > 0 &&
        !owners.some(
            (o) => o.value.toLowerCase() === trimmedOwnerSearch.toLowerCase()
        )

    // Set default owner on first load (use functional update to avoid stale closure)
    useEffect(() => {
        if (owners.length > 0) {
            setFormData((prev) =>
                prev.owner ? prev : { ...prev, owner: owners[0].value }
            )
        }
    }, [owners])

    const handleAddChild = () => {
        setChildComponents([
            ...childComponents,
            {
                id: crypto.randomUUID(),
                model_id: '',
                model_label: '',
                category: '',
                hasTags: 'yes',
                serviceTags: [],
                quantity: '',
                condition: 'new',
            },
        ])
    }

    const handleUpdateChild = (index: number, data: ChildComponentFormData) => {
        setChildComponents((prev) => {
            const updated = [...prev]
            updated[index] = data
            return updated
        })
    }

    const handleRemoveChild = (index: number) => {
        setChildComponents((prev) => prev.filter((_, i) => i !== index))
    }

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Tab' || e.key === 'Enter') {
            e.preventDefault()
            handleAddTags()
        }
    }

    const handleAddTags = () => {
        if (!tagInput.trim()) return
        const newTags = tagInput
            .split(/[\s,\n]+/)
            .map((t) => t.trim())
            .filter((t) => t && !serviceTags.includes(t))
        setServiceTags([...serviceTags, ...newTags])
        setTagInput('')
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData('text')
        const newTags = pastedText
            .split(/[\s,\n]+/)
            .map((t) => t.trim())
            .filter((t) => t && !serviceTags.includes(t))
        setServiceTags([...serviceTags, ...newTags])
        setTagInput('')
    }

    const handleRemoveTag = (tag: string) => {
        setServiceTags(serviceTags.filter((t) => t !== tag))
    }

    const resetForm = useCallback(() => {
        setFormData({
            model_id: '',
            form_factor: '',
            name: '',
            purchase_price: '',
            condition: 'new',
            owner: owners.length > 0 ? owners[0].value : '',
            description: '',
        })
        setServiceTags([])
        setQuantity('')
        setTagInput('')
        setChildComponents([])
        setHasTags('yes')
        resetDates({
            import_date: today,
            warranty_start: today,
            warranty_end: today,
        })
    }, [owners, today, resetDates])

    const handleSubmit = () => {
        if (hasTags === 'yes' && serviceTags.length === 0) return
        if (hasTags === 'no' && (!quantity || parseInt(quantity) <= 0)) return
        if (!selectedModel) return

        const dateValues = getValues()

        // Build children array
        const children: ImportItemMetadata[] = childComponents
            .filter((child) => {
                if (!child.model_id) return false
                if (child.hasTags === 'yes' && child.serviceTags.length === 0)
                    return false
                if (
                    child.hasTags === 'no' &&
                    (!child.quantity || parseInt(child.quantity) <= 0)
                )
                    return false
                return true
            })
            .map((child) => {
                const childModel = componentModels.find(
                    (m) => m.value === child.model_id
                )
                return {
                    service_tags:
                        child.hasTags === 'yes' ? child.serviceTags : [],
                    quantity:
                        child.hasTags === 'no'
                            ? parseInt(child.quantity)
                            : undefined,
                    model_id: child.model_id,
                    item_type: 'component' as const,
                    category:
                        childModel?.category.toLowerCase() || child.category,
                    name: childModel?.label || child.model_label,
                    purchase_date: toRFC3339(dateValues.import_date),
                    warranty_start: toRFC3339(dateValues.warranty_start),
                    warranty_end: toRFC3339(dateValues.warranty_end),
                    status: 'available',
                    condition: child.condition,
                }
            })

        const itemData: ImportItemMetadata = {
            service_tags: hasTags === 'yes' ? serviceTags : [],
            quantity: hasTags === 'no' ? parseInt(quantity) : undefined,
            model_id: formData.model_id,
            item_type: selectedModel.item_type,
            category: selectedModel.category.toLowerCase(),
            name:
                formData.name ||
                `${selectedModel.category}${hasTags === 'yes' && serviceTags[0] ? ` - ${serviceTags[0]}` : ''}`,
            purchase_price: formData.purchase_price
                ? parseFloat(formData.purchase_price)
                : undefined,
            purchase_date: toRFC3339(dateValues.import_date),
            warranty_start: toRFC3339(dateValues.warranty_start),
            warranty_end: toRFC3339(dateValues.warranty_end),
            status: 'available',
            condition: formData.condition,
            owner: formData.owner || undefined,
            description: formData.description || undefined,
            children: children.length > 0 ? children : undefined,
        }

        onAdd(itemData)

        if (!isEditMode) {
            resetForm()
            setShowForm(false)
        }
    }

    const isValid =
        selectedModel &&
        (hasTags === 'yes'
            ? serviceTags.length > 0
            : quantity && parseInt(quantity) > 0)

    // Collapsed state for create mode
    if (!isEditMode && !showForm) {
        return (
            <Button
                type="button"
                variant="outlined"
                onClick={() => setShowForm(true)}
                className="w-full"
            >
                <Plus className="w-4 h-4 mr-2" />
                Thêm hàng mới
            </Button>
        )
    }

    const formContent = (
        <div className="space-y-4">
            {/* Radio: Has service tags or not */}
            <div className="flex flex-col space-y-2">
                <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                        type="radio"
                        name={`hasTags-${mode}`}
                        value="yes"
                        checked={hasTags === 'yes'}
                        onChange={() => setHasTags('yes')}
                        className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm">
                        Có service tag / series number
                    </span>
                </label>
                <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                        type="radio"
                        name={`hasTags-${mode}`}
                        value="no"
                        checked={hasTags === 'no'}
                        onChange={() => setHasTags('no')}
                        className="w-4 h-4 text-primary"
                    />
                    <span className="text-sm">Không service tag</span>
                </label>
            </div>

            {/* Service Tags or Quantity */}
            {hasTags === 'yes' ? (
                <div className="space-y-2">
                    <Label className="text-sm">
                        Service tag / series number *
                    </Label>
                    <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onBlur={handleAddTags}
                        placeholder="Nhập và nhấn Tab để thêm..."
                    />
                    {serviceTags.length > 0 && (
                        <div className="flex flex-wrap gap-1 p-2 border rounded bg-muted/30">
                            {serviceTags.map((tag) => (
                                <Badge
                                    key={tag}
                                    variant="secondary"
                                    className="gap-1 pr-1 text-xs"
                                >
                                    {tag}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveTag(tag)}
                                        className="hover:bg-muted rounded-full p-0.5"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                    <div className="text-xs text-muted-foreground">
                        {serviceTags.length} item(s)
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    <Label className="text-sm">Số lượng *</Label>
                    <Input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="Nhập số lượng..."
                        className="w-32"
                    />
                </div>
            )}

            {/* Model Picker */}
            <div className="space-y-2">
                <Label className="text-sm">Model *</Label>
                <Popover
                    open={modelPickerOpen}
                    onOpenChange={setModelPickerOpen}
                >
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            role="combobox"
                            aria-expanded={modelPickerOpen}
                            disabled={isLoadingModels}
                            className={cn(
                                'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
                                'focus:outline-none focus:ring-2 focus:ring-ring',
                                'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                        >
                            {isLoadingModels ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : selectedModel ? (
                                <span className="truncate text-left">
                                    {selectedModel.label}
                                    {selectedModel.brand &&
                                        ` - ${selectedModel.brand}`}
                                </span>
                            ) : (
                                <span className="text-muted-foreground">
                                    Chọn model...
                                </span>
                            )}
                            <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0" align="start">
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder="Tìm model..."
                                value={modelSearch}
                                onValueChange={setModelSearch}
                            />
                            <CommandList className="max-h-[300px]">
                                {Object.keys(filteredModelsByCategory)
                                    .length === 0 ? (
                                    <CommandEmpty>
                                        Không tìm thấy model.
                                    </CommandEmpty>
                                ) : (
                                    Object.entries(
                                        filteredModelsByCategory
                                    ).map(([category, categoryModels]) => (
                                        <CommandGroup
                                            key={category}
                                            heading={category}
                                        >
                                            {categoryModels.map((model) => (
                                                <CommandItem
                                                    key={model.value}
                                                    value={model.value}
                                                    onSelect={() => {
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            model_id:
                                                                model.value,
                                                            name: model.label,
                                                        }))
                                                        setModelPickerOpen(
                                                            false
                                                        )
                                                        setModelSearch('')
                                                    }}
                                                >
                                                    <Check
                                                        className={cn(
                                                            'mr-2 h-4 w-4',
                                                            formData.model_id ===
                                                                model.value
                                                                ? 'opacity-100'
                                                                : 'opacity-0'
                                                        )}
                                                    />
                                                    <span className="truncate">
                                                        {model.label}
                                                        {model.brand &&
                                                            ` - ${model.brand}`}
                                                    </span>
                                                </CommandItem>
                                            ))}
                                        </CommandGroup>
                                    ))
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>

            {/* Model Specifications */}
            {selectedModel?.specifications &&
                selectedModel.specifications.length > 0 && (
                    <div className="p-2 bg-muted/30 rounded text-xs">
                        <span className="text-muted-foreground">Specs: </span>
                        {selectedModel.specifications.map((spec, i) => (
                            <span key={spec.id}>
                                {spec.name}: {spec.value}
                                {spec.unit && ` ${spec.unit}`}
                                {i < selectedModel.specifications!.length - 1 &&
                                    ' | '}
                            </span>
                        ))}
                    </div>
                )}

            {/* Child Components - Only for equipment */}
            {selectedModel?.item_type === 'equipment' && (
                <div className="space-y-2 pt-3 border-t">
                    <div className="flex items-center justify-between">
                        <Label className="text-sm">Thành phần con</Label>
                        <Button
                            variant="outlined"
                            size="small"
                            type="button"
                            onClick={handleAddChild}
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>

                    {childComponents.length > 0 && (
                        <div className="space-y-2">
                            {childComponents.map((child, idx) => (
                                <ChildComponentCard
                                    key={child.id}
                                    data={child}
                                    models={componentModels}
                                    isLoadingModels={isLoadingModels}
                                    onUpdate={(updated) =>
                                        handleUpdateChild(idx, updated)
                                    }
                                    onRemove={() => handleRemoveChild(idx)}
                                />
                            ))}
                        </div>
                    )}

                    {childComponents.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                            Thêm các thành phần như CPU, RAM, Disk.
                        </p>
                    )}
                </div>
            )}

            {/* Price & Condition */}
            <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                    <Label className="text-sm">Giá nhập</Label>
                    <Input
                        type="number"
                        value={formData.purchase_price}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                purchase_price: e.target.value,
                            }))
                        }
                        placeholder="0"
                    />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm">Tình trạng</Label>
                    <Select
                        value={formData.condition}
                        onValueChange={(v) =>
                            setFormData((prev) => ({ ...prev, condition: v }))
                        }
                    >
                        <SelectTrigger className="h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {CONDITIONS.map((c) => (
                                <SelectItem key={c.value} value={c.value}>
                                    {c.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Owner — chọn owner có sẵn hoặc gõ để thêm owner mới */}
            <div className="space-y-2">
                <Label className="text-sm">Chủ sở hữu</Label>
                <Popover
                    open={ownerPickerOpen}
                    onOpenChange={setOwnerPickerOpen}
                >
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            role="combobox"
                            aria-expanded={ownerPickerOpen}
                            className={cn(
                                'flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm',
                                'focus:outline-none focus:ring-2 focus:ring-ring',
                                'disabled:cursor-not-allowed disabled:opacity-50'
                            )}
                        >
                            {formData.owner ? (
                                <span className="truncate text-left">
                                    {formData.owner}
                                </span>
                            ) : (
                                <span className="text-muted-foreground">
                                    Chọn hoặc thêm owner...
                                </span>
                            )}
                            <ChevronsUpDown className="ml-1 h-4 w-4 shrink-0 opacity-50" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0" align="start">
                        <Command shouldFilter={false}>
                            <CommandInput
                                placeholder="Tìm hoặc nhập owner..."
                                value={ownerSearch}
                                onValueChange={setOwnerSearch}
                                maxLength={100}
                            />
                            <CommandList className="max-h-[300px]">
                                {filteredOwners.length === 0 &&
                                !canCreateOwner ? (
                                    <CommandEmpty>
                                        Không có owner phù hợp.
                                    </CommandEmpty>
                                ) : (
                                    <CommandGroup>
                                        {filteredOwners.map((o) => (
                                            <CommandItem
                                                key={o.value}
                                                value={o.value}
                                                onSelect={() => {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        owner: o.value,
                                                    }))
                                                    setOwnerPickerOpen(false)
                                                    setOwnerSearch('')
                                                }}
                                            >
                                                <Check
                                                    className={cn(
                                                        'mr-2 h-4 w-4',
                                                        formData.owner ===
                                                            o.value
                                                            ? 'opacity-100'
                                                            : 'opacity-0'
                                                    )}
                                                />
                                                <span className="truncate">
                                                    {o.label}
                                                </span>
                                            </CommandItem>
                                        ))}
                                        {canCreateOwner && (
                                            <CommandItem
                                                value={`__create_owner__${trimmedOwnerSearch}`}
                                                onSelect={() => {
                                                    setFormData((prev) => ({
                                                        ...prev,
                                                        owner: trimmedOwnerSearch.slice(
                                                            0,
                                                            100
                                                        ),
                                                    }))
                                                    setOwnerPickerOpen(false)
                                                    setOwnerSearch('')
                                                }}
                                            >
                                                <Plus className="mr-2 h-4 w-4" />
                                                <span className="truncate">
                                                    Thêm &quot;
                                                    {trimmedOwnerSearch}
                                                    &quot;
                                                </span>
                                            </CommandItem>
                                        )}
                                    </CommandGroup>
                                )}
                            </CommandList>
                        </Command>
                    </PopoverContent>
                </Popover>
            </div>

            {/* Dates */}
            <FormItem
                control={control}
                name="import_date"
                type="datepicker"
                label="Ngày nhập"
            />

            <div className="grid grid-cols-2 gap-3">
                <FormItem
                    control={control}
                    name="warranty_start"
                    type="datepicker"
                    label="Bảo hành từ"
                />
                <FormItem
                    control={control}
                    name="warranty_end"
                    type="datepicker"
                    label="Bảo hành đến"
                />
            </div>

            {/* Description */}
            <div className="space-y-2">
                <Label className="text-sm">Mô tả</Label>
                <Input
                    value={formData.description}
                    onChange={(e) =>
                        setFormData((prev) => ({
                            ...prev,
                            description: e.target.value,
                        }))
                    }
                    placeholder="Mô tả (tùy chọn)..."
                />
            </div>
        </div>
    )

    // Edit mode - render in Card
    if (isEditMode) {
        return (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg">Chỉnh sửa hàng</CardTitle>
                </CardHeader>
                <CardContent>
                    {formContent}

                    {/* Footer */}
                    <div className="flex justify-end gap-2 pt-4 mt-4 border-t">
                        <Button
                            variant="outlined"
                            size="small"
                            type="button"
                            onClick={props.onCancel}
                        >
                            Hủy
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            type="button"
                            onClick={handleSubmit}
                            disabled={!isValid}
                        >
                            Lưu{' '}
                            {hasTags === 'yes'
                                ? serviceTags.length > 0 &&
                                  `(${serviceTags.length})`
                                : quantity &&
                                  parseInt(quantity) > 0 &&
                                  `(${quantity})`}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        )
    }

    // Create mode - render in collapsible Card
    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Thêm hàng mới</CardTitle>
                    <Button
                        variant="text"
                        size="small"
                        type="button"
                        onClick={() => {
                            resetForm()
                            setShowForm(false)
                        }}
                    >
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {formContent}

                {/* Footer */}
                <div className="flex justify-end pt-4 mt-4 border-t">
                    <Button
                        variant="contained"
                        size="small"
                        type="button"
                        onClick={handleSubmit}
                        disabled={!isValid}
                    >
                        <Plus className="w-4 h-4 mr-1" />
                        Thêm{' '}
                        {hasTags === 'yes'
                            ? serviceTags.length > 0 &&
                              `(${serviceTags.length})`
                            : quantity &&
                              parseInt(quantity) > 0 &&
                              `(${quantity})`}
                    </Button>
                </div>
            </CardContent>
        </Card>
    )
}

export default ItemForm
